import { type FC, type ReactNode } from "react";

import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { HTML } from "@styled/html";
import { Prose } from "@styled/prose";

// A transcript beside its rail: the reading column takes what the rail
// leaves, and the rail drops below it on narrow screens.
export const TranscriptLayout: FC<{ rail: ReactNode; children: ReactNode }> = ({
  rail,
  children,
}) => (
  <div className="flex max-w-[1200px] flex-wrap items-start gap-6">
    <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-4">
      {children}
    </div>
    <aside className="flex flex-[1_1_220px] flex-col gap-4">{rail}</aside>
  </div>
);

// The view switch at the left, the actions at the right.
export const TranscriptToolbar: FC<{
  leading: ReactNode;
  trailing: ReactNode;
}> = ({ leading, trailing }) => (
  <div className="flex flex-wrap items-center gap-2.5">
    {leading}
    <div className="ml-auto">{trailing}</div>
  </div>
);

// The file being read, lit: what it is, its facts, and its player.
export const TranscriptHead: FC<{
  icon: IconProp;
  title: string;
  meta: string;
  children?: ReactNode;
}> = ({ icon, title, meta, children }) => (
  <section className="surface-lit flex flex-wrap items-center gap-4 rounded-[18px] px-[22px] py-[18px]">
    <span className="flex size-11 shrink-0 items-center justify-center rounded-[13px] bg-linear-to-b from-white to-[#eaf6f4] text-indigo-700 shadow-[inset_0_1px_0_#fff] ring-1 ring-indigo-500/25">
      <FontAwesomeIcon icon={icon} />
    </span>
    <div className="min-w-0">
      <h2 className="text-ink text-[17px] font-semibold tracking-[-0.02em]">
        {title}
      </h2>
      <div className="text-muted mt-0.5 text-[12.5px]">{meta}</div>
    </div>
    {children}
  </section>
);

// The transcript itself: server-sanitized HTML, or the verbatim text.
export const TranscriptBody: FC<
  { html: string; text?: never } | { text: string; html?: never }
> = ({ html, text }) => (
  <section className="surface-resting rounded-[18px] px-[30px] pt-[26px] pb-[30px]">
    <Prose>
      {html !== undefined ? (
        <HTML html={html} />
      ) : (
        <p className="whitespace-pre-wrap">{text}</p>
      )}
    </Prose>
  </section>
);

// What shows in place of the transcript while it's made, or if it failed.
export const TranscriptNote: FC<{ children: ReactNode }> = ({ children }) => (
  <section className="surface-quiet text-muted rounded-[18px] px-[30px] py-6 text-sm">
    {children}
  </section>
);
